import React from 'react'

import {
  pantryColors,
  pantryRadii,
  pantryShadows,
} from '../../../foundations/tokens'

type ToastVariant = 'default' | 'success' | 'error'

interface ToastProps {
  message: string
  variant?: ToastVariant
  onDismiss?: () => void
  // Optional link-style button after the message, e.g. "Open campaign".
  action?: { label: string; onClick: () => void }
}

const variantConfig: Record<
  ToastVariant,
  { background: string; color: string; icon: React.ReactNode }
> = {
  default: {
    background: pantryColors.gray[800],
    color: pantryColors.white,
    icon: (
      <svg
        width="16"
        height="16"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  success: {
    background: pantryColors.brand.primary,
    color: pantryColors.white,
    icon: (
      <svg
        width="16"
        height="16"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 13l4 4L19 7"
        />
      </svg>
    ),
  },
  error: {
    background: pantryColors.red[700],
    color: pantryColors.white,
    icon: (
      <svg
        width="16"
        height="16"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    ),
  },
}

export const Toast: React.FC<ToastProps> = ({
  message,
  variant = 'default',
  onDismiss,
  action,
}) => {
  const config = variantConfig[variant]
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        borderRadius: pantryRadii.md,
        boxShadow: pantryShadows.lg,
        fontSize: 13,
        fontWeight: 500,
        background: config.background,
        color: config.color,
      }}
      role="alert"
    >
      <span style={{ flexShrink: 0, opacity: 0.9 }}>{config.icon}</span>
      <span>{message}</span>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          style={{
            flexShrink: 0,
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: 'inherit',
            font: 'inherit',
            fontWeight: 700,
            textDecoration: 'underline',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {action.label}
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          style={{
            marginLeft: 8,
            flexShrink: 0,
            opacity: 0.7,
            border: 'none',
            background: 'transparent',
            color: 'inherit',
            cursor: 'pointer',
          }}
          aria-label="Dismiss"
        >
          <svg
            width="16"
            height="16"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  )
}
